import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, SEO_CONFIG } from "@/utils/helpers";
import { 
  generateOrganizationSchema, 
  generateWebSiteSchema, 
  generateBrandListSchema,
  generateCollectionPageSchema,
  combineSchemas,
  useStructuredData
} from "@/utils/structuredData";

const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [topBrands, setTopBrands] = useState([]);
  const [topQueries, setTopQueries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [brandsRes, queriesRes] = await Promise.all([
          axios.get(`${API}/brands/top?limit=30`),
          axios.get(`${API}/queries/top?limit=30`)
        ]);
        
        setTopBrands(brandsRes.data.map(b => b.brand));
        setTopQueries(queriesRes.data.map(q => q.full_query));
      } catch (error) {
        console.error('Error fetching top brands/queries:', error);
        setTopBrands([]);
        setTopQueries([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const suggestions = searchQuery.trim() 
    ? topQueries.filter(q => 
        q.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8)
    : [];

  const handleSearch = (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    setShowSuggestions(false);
    navigate(`/?query=${encodeURIComponent(searchQuery)}&page=1`);
  };

  const handleSuggestionClick = (query) => {
    setSearchQuery(query);
    setShowSuggestions(false);
    navigate(`/?query=${encodeURIComponent(query)}&page=1`);
  };

  const handleQuickSearch = (query) => {
    navigate(`/?query=${encodeURIComponent(query)}&page=1`);
  };

  // Schema.org structured data for homepage - comprehensive SEO
  const structuredData = combineSchemas(
    generateOrganizationSchema(),
    generateWebSiteSchema(),
    generateCollectionPageSchema(),
    topBrands.length > 0 ? generateBrandListSchema(topBrands) : null
  );
  
  // Inject structured data into document head
  useStructuredData(structuredData, 'homepage-structured-data');
  
  if (loading) {
    return (
      <div className="homepage">
        <Helmet>
          <title>{SEO_CONFIG.defaultTitle}</title>
        </Helmet>
        <header className="homepage-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_shop-navigator-13/artifacts/hmk599ai_marques-and-co_logo.jpg" 
            alt="Marques & Co" 
            className="homepage-logo"
          />
        </header>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="homepage">
      <Helmet>
        <title>{SEO_CONFIG.defaultTitle}</title>
        <meta name="description" content={SEO_CONFIG.defaultDescription} />
        <meta name="keywords" content={SEO_CONFIG.defaultKeywords} />
        <meta property="og:title" content={SEO_CONFIG.defaultTitle} />
        <meta property="og:description" content={SEO_CONFIG.defaultDescription} />
        <meta property="og:type" content={SEO_CONFIG.type} />
        <meta property="og:url" content={SEO_CONFIG.siteUrl} />
        <meta property="og:site_name" content={SEO_CONFIG.siteName} />
        <meta property="og:locale" content={SEO_CONFIG.locale} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={SEO_CONFIG.defaultTitle} />
        <meta name="twitter:description" content={SEO_CONFIG.defaultDescription} />
        <link rel="canonical" href={SEO_CONFIG.siteUrl} />
      </Helmet>

      <header className="homepage-header">
        <img 
          src="https://customer-assets.emergentagent.com/job_shop-navigator-13/artifacts/hmk599ai_marques-and-co_logo.jpg" 
          alt="Marques & Co" 
          className="homepage-logo"
        />
      </header>

      <section className="hero-section">
        <h1 className="hero-title">Trouvez les meilleures offres</h1>
        <p className="hero-subtitle">Comparez des milliers de produits de vos marques préférées</p>

        <form onSubmit={handleSearch} className="hero-search">
          <div className="hero-search-wrapper">
            <Search className="hero-search-icon" />
            <Input
              data-testid="homepage-search-input"
              type="text"
              placeholder="Rechercher un produit ou une marque..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="hero-search-input"
            />
            <Button data-testid="homepage-search-button" type="submit" className="hero-search-button">
              Rechercher
            </Button>
          </div>
          
          {showSuggestions && suggestions.length > 0 && (
            <div className="search-suggestions">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="suggestion-item"
                  data-testid={`suggestion-${suggestion.replace(/\s+/g, '-')}`}
                >
                  <Search className="suggestion-icon" />
                  <span>{suggestion}</span>
                </button>
              ))}
            </div>
          )}
        </form>
      </section>

      <section className="brands-section">
        <h2 className="section-title">Marques populaires</h2>
        <div className="brands-grid">
          {topBrands.map((brand) => (
            <a
              key={brand}
              href={`/brand/${encodeURIComponent(brand)}`}
              className="brand-card"
              data-testid={`brand-${brand.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="brand-name">{brand}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="searches-section">
        <h2 className="section-title">Recherches populaires</h2>
        <div className="searches-grid">
          {topQueries.map((query) => (
            <button
              key={query}
              onClick={() => handleQuickSearch(query)}
              className="search-chip"
              data-testid={`search-${query.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Search className="chip-icon" />
              <span>{query}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;
